/* -----------------------------------------------------------------------------
 * Layout.jsx — Minimal header with company name + "Back to OGPumper" redirect
 *  - NO other features
 *  - Company name left-aligned (to match screenshot vibe)
 *  - Left-side subtle back button → {subdomain}.ogpumper.com/{appPath}
 *  - Chart fills remaining viewport below header
 * ----------------------------------------------------------------------------- */
import React, { useEffect, useMemo } from "react";

/* Validate/subtle-sanitize a subdomain */
function sanitizeSubdomain(raw) {
  if (!raw) return "";
  const v = String(raw).trim().toLowerCase();
  return /^[a-z0-9-]+$/.test(v) ? v : "";
}

/* Ensure appPath is a safe relative path (no protocol/domain) */
function sanitizeAppPath(raw) {
  if (!raw) return "";
  let v = String(raw).trim();
  v = v.replace(/^[a-z]+:\/\/[^/]+/i, ""); // strip protocol+host if present
  v = v.replace(/^\/+/, "");              // no leading slashes
  v = v.replace(/[^a-z0-9/_\-]/gi, "");   // safe chars only
  return v;
}

function computeBackUrl(subdomain, appPath) {
  const cleanSub = sanitizeSubdomain(subdomain);
  const cleanApp = sanitizeAppPath(appPath);

  // If both subdomain and appPath are provided, go to {sub}.ogpumper.com/{appPath}
  if (cleanSub && cleanApp) {
    return `https://${cleanSub}.ogpumper.com/ogp_Home.php`;
  }

  // If only subdomain is provided, fall back to the original ogpumper.com host
  if (cleanSub) {
    return `https://${cleanSub}.ogpumper.com/`;
  }

  // Otherwise, try to return to wherever the user came from on ogpumper.com
  try {
    const ref = document.referrer ? new URL(document.referrer) : null;
    if (ref && /(^|\.)ogpumper\.com$/i.test(ref.hostname)) {
      return ref.href; // return to the referring page on ogpumper.com
    }
  } catch (_) {
    // ignore malformed URLs
  }

  // Default fallback
  return "https://ogpumper.com/";
}

export default function Layout({ companyName, subdomain, appPath, children }) {
  const backUrl = useMemo(() => computeBackUrl(subdomain, appPath), [subdomain, appPath]);

  // Optional: keyboard shortcut Alt+H to go back to OGPumper
  useEffect(() => {
    const onKey = (e) => {
      if (e.altKey && (e.key === "h" || e.key === "H")) {
        e.preventDefault();
        window.location.assign(backUrl);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [backUrl]);

  return (
    <div className="min-h-screen w-full bg-white text-slate-900">
      {/* ── Header: back button (left) + company name (left, bold) ───────────── */}
      <header
        className="
          sticky top-0 z-30
          w-full border-b border-slate-200/70
          bg-white/80 backdrop-blur supports-[backdrop-filter]:bg-white/60
        "
        role="banner"
        aria-label="Application header"
      >
        <div className="mx-auto max-w-screen-xl px-4">
          <div className="relative flex h-14 items-center">
            {/* Back to OGPumper (left) */}
            <div className="flex items-center">
              <button
                type="button"
                onClick={() => window.location.assign(backUrl)}
                title="Back to ogPumper (Alt+H)"
                className="
                  inline-flex items-center gap-2 rounded-md
                  border border-slate-200 bg-white/90 px-3 py-1.5 text-xs font-medium
                  text-slate-700 shadow-sm hover:bg-slate-50 active:scale-95
                "
              >
                <span className="inline-flex h-5 w-5 items-center justify-center text-[14px]">
                  ←
                </span>
                <span className="hidden sm:inline">Back to ogPumper</span>
                <span className="sm:hidden">Back</span>
              </button>
            </div>

            {/* Company name (left-aligned, bold; no initial badge) */}
            <div className="ml-4">
              <span className="text-lg font-extrabold tracking-tight text-slate-900">
                {companyName || "OGPumper"}
              </span>
            </div>
          </div>
        </div>
      </header>

      {/* ── Main: chart should occupy the full remaining viewport height ──── */}
      <main
        className="
          w-full
          min-h-[calc(100vh-56px)]
          /* 56px = h-14 header height; gives the rest to the chart */
        "
        role="main"
      >
        {children}
      </main>
    </div>
  );
}
